import type {
  ClaireReport,
  ConversationChannel,
  ReplySpeed,
  ReportWindowInput,
} from '@borradh-workspace/api-client/types';
import {
  Clock,
  MessagesSquare,
  Sparkles,
  Target,
  UsersRound,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { useGetClaireReport } from '../api';
import {
  WEEKDAYS,
  countOf,
  describeChange,
  formatCount,
  formatDuration,
  formatPercent,
} from '../lib/format';
import { DailyStackedBars, SERIES_COLOR } from './report-charts';
import {
  Figure,
  Legend,
  Panel,
  ReportSection,
  SERIES,
  SplitBar,
  StatCard,
} from './report-primitives';
import { ReportState } from './report-state';

const CHANNEL_LABELS: Record<ConversationChannel, string> = {
  whatsapp: 'WhatsApp',
  instagram_dm: 'Instagram',
  facebook_messenger: 'Messenger',
  sms: 'SMS',
};

export function ClaireReportView({ range }: { range: ReportWindowInput }) {
  const { report, isLoading, isError, refetch } = useGetClaireReport(range);
  return (
    <ReportState
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
    >
      {report ? <ClaireReportBody report={report} /> : null}
    </ReportState>
  );
}

function ClaireReportBody({ report }: { report: ClaireReport }) {
  const { headline, conversations, replySpeed, outcomes } = report;
  const claireReplied = conversations.claireOnly + conversations.claireThenTeam;
  const share = (part: number) =>
    formatPercent(conversations.total > 0 ? part / conversations.total : null);

  return (
    <div className="flex flex-col gap-10">
      <Summary report={report} />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4">
        <StatCard
          label="Conversations"
          value={formatCount(headline.conversations.value)}
          change={describeChange(
            headline.conversations.value,
            headline.conversations.previous
          )}
          footnote={`${countOf(conversations.messages.fromClients, 'message')} from clients`}
        />
        <StatCard
          label="Handled by Claire alone"
          value={formatCount(headline.handledByClaire.value)}
          change={describeChange(
            headline.handledByClaire.value,
            headline.handledByClaire.previous
          )}
          footnote={`${share(conversations.claireOnly)} of conversations needed no one`}
        />
        <StatCard
          label="Claire's first reply"
          value={formatDuration(replySpeed.firstReplyMedianSeconds.claire)}
          footnote={`Your team: ${formatDuration(replySpeed.firstReplyMedianSeconds.team)} (typical)`}
        />
        <StatCard
          label="New contacts"
          value={formatCount(headline.newContacts.value)}
          change={describeChange(
            headline.newContacts.value,
            headline.newContacts.previous
          )}
          footnote="New clients who started with a message"
        />
      </div>

      <ReportSection
        id="reply-speed"
        icon={<Clock />}
        title="How fast clients hear back"
        description="Measured from a client's message to the first reply"
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-4">
          <SpeedCard
            who="Claire"
            icon={<Sparkles />}
            speed={replySpeed.claire}
            firstReply={replySpeed.firstReplyMedianSeconds.claire}
            accent
          />
          <SpeedCard
            who="Your team"
            icon={<UsersRound />}
            speed={replySpeed.team}
            firstReply={replySpeed.firstReplyMedianSeconds.team}
          />
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 rounded-xl border bg-card p-5 shadow-xs">
          <Figure
            label="Waiting on a reply now"
            value={formatCount(conversations.waitingNow)}
            hint="Last message is the client's, over an hour ago"
          />
          <Figure
            label="Never answered"
            value={formatCount(replySpeed.unanswered)}
            hint="Times a client wrote and no one replied"
          />
          <Figure
            label="Waited over an hour"
            value={formatCount(
              replySpeed.claire.overAnHour + replySpeed.team.overAnHour
            )}
            hint="Before anyone replied"
          />
        </div>
      </ReportSection>

      <ReportSection
        id="handling"
        icon={<MessagesSquare />}
        title="Who handled conversations"
        description={`Claire replied in ${formatCount(claireReplied)} of ${formatCount(conversations.total)} conversations`}
      >
        <Panel>
          <SplitBar
            label="Who replied"
            segments={[
              {
                label: 'Claire alone',
                value: conversations.claireOnly,
                color: SERIES.primary,
              },
              {
                label: 'Claire, then your team',
                value: conversations.claireThenTeam,
                color: SERIES.upcoming,
              },
              {
                label: 'Your team only',
                value: conversations.teamOnly,
                color: SERIES.secondary,
              },
              {
                label: 'No reply yet',
                value: conversations.unanswered,
                color: SERIES.muted,
              },
            ]}
          />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-5 border-t pt-4">
            <Figure
              label="Messages per conversation"
              value={conversations.averageMessages?.toFixed(1) ?? '—'}
            />
            <Figure
              label="When Claire handles it"
              value={conversations.averageMessagesClaireOnly?.toFixed(1) ?? '—'}
            />
            <Figure
              label="When your team joins"
              value={conversations.averageMessagesWithTeam?.toFixed(1) ?? '—'}
            />
            <Figure
              label="Claire wrote, no answer"
              value={formatCount(conversations.claireOutreach)}
              hint="Reminders and follow-ups, not counted above"
            />
          </div>
        </Panel>
        <Panel
          title="Messages by day"
          description="Every message sent and received, reminders included"
          action={
            <Legend
              items={[
                { label: 'Clients', color: SERIES.tertiary },
                { label: 'Claire', color: SERIES.primary },
                { label: 'Your team', color: SERIES.secondary },
              ]}
            />
          }
        >
          <DailyStackedBars
            label="Messages by day"
            data={report.byDay}
            series={[
              {
                key: 'fromClients',
                label: 'Clients',
                color: SERIES_COLOR.tertiary,
              },
              {
                key: 'fromClaire',
                label: 'Claire',
                color: SERIES_COLOR.primary,
              },
              {
                key: 'fromTeam',
                label: 'Your team',
                color: SERIES_COLOR.secondary,
              },
            ]}
          />
        </Panel>
      </ReportSection>

      <ReportSection
        id="outcomes"
        icon={<Target />}
        title="What came of it"
        description="Bookings, deposits and new contacts from conversations"
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 rounded-xl border bg-card p-5 shadow-xs">
          <Figure
            label="Booked by Claire"
            value={formatCount(outcomes.bookings)}
            hint={`${formatCount(outcomes.bookingsByChannel.chat)} by message · ${formatCount(outcomes.bookingsByChannel.phone)} by phone`}
          />
          <Figure
            label="Chats that led to a booking"
            value={formatCount(outcomes.chatsThatBooked)}
            hint={`${share(outcomes.chatsThatBooked)} of conversations, within 14 days`}
          />
          <Figure
            label="Deposit links sent by Claire"
            value={formatCount(outcomes.depositsSent)}
            hint={`${formatCount(outcomes.depositsPaid)} paid`}
          />
          <Figure
            label="New contacts"
            value={formatCount(outcomes.newContacts)}
            hint="Added to Customers from a chat"
          />
        </div>
      </ReportSection>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,400px),1fr))] gap-4">
        <Panel title="By channel" description="Conversations and messages sent">
          {report.byChannel.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No conversations in this period.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs">
                <tr className="text-left">
                  <th className="pb-2 font-medium">Channel</th>
                  <th className="pb-2 pl-3 text-right font-medium">Chats</th>
                  <th className="pb-2 pl-3 text-right font-medium">
                    From Claire
                  </th>
                  <th className="pb-2 pl-3 text-right font-medium">
                    From team
                  </th>
                  <th className="pb-2 pl-3 text-right font-medium">
                    First reply
                  </th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {report.byChannel.map((row) => (
                  <tr key={row.channel} className="border-t">
                    <td className="py-2.5 font-medium">
                      {CHANNEL_LABELS[row.channel]}
                    </td>
                    <td className="py-2.5 pl-3 text-right font-semibold">
                      {formatCount(row.conversations)}
                    </td>
                    <td className="py-2.5 pl-3 text-right text-muted-foreground">
                      {formatCount(row.fromClaire)}
                    </td>
                    <td className="py-2.5 pl-3 text-right text-muted-foreground">
                      {formatCount(row.fromTeam)}
                    </td>
                    <td className="py-2.5 pl-3 text-right">
                      {formatDuration(row.medianFirstReplySeconds)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        <Panel
          title="When clients message"
          description="Client messages by day and time"
        >
          <BusyTimes cells={report.busyTimes} />
        </Panel>
      </div>
    </div>
  );
}

/** One plain sentence on top: the thing an owner opens this tab to learn. */
function Summary({ report }: { report: ClaireReport }) {
  const { conversations, replySpeed } = report;
  if (conversations.total === 0) {
    return (
      <div className="rounded-xl border border-dashed p-6 text-center text-muted-foreground text-sm">
        No conversations in this period.
      </div>
    );
  }
  const claireReplied = conversations.claireOnly + conversations.claireThenTeam;
  return (
    <div className="flex items-start gap-4 rounded-xl border border-primary/20 bg-primary/5 p-5">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Sparkles className="size-5" aria-hidden />
      </span>
      <div>
        <p className="font-semibold text-lg tracking-tight">
          {claireReplied === 0
            ? 'Claire didn’t reply to any conversations in this period'
            : `Claire handled ${formatCount(conversations.claireOnly)} of ${formatCount(conversations.total)} conversations on her own`}
        </p>
        <p className="mt-1 text-muted-foreground text-sm">
          {replySpeed.claire.replies > 0
            ? `She replied ${countOf(replySpeed.claire.replies, 'time')}, typically in ${formatDuration(replySpeed.claire.medianSeconds)}. `
            : ''}
          {replySpeed.team.replies > 0
            ? `Your team replied ${countOf(replySpeed.team.replies, 'time')}, typically in ${formatDuration(replySpeed.team.medianSeconds)}.`
            : 'Your team didn’t need to reply.'}
        </p>
      </div>
    </div>
  );
}

function SpeedCard({
  who,
  icon,
  speed,
  firstReply,
  accent,
}: {
  who: string;
  icon: ReactNode;
  speed: ReplySpeed;
  firstReply: number | null;
  accent?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-xl border p-5 shadow-xs',
        accent ? 'border-primary/20 bg-primary/5' : 'bg-card'
      )}
    >
      <p
        className={cn(
          'flex items-center gap-1.5 font-semibold text-sm [&_svg]:size-4',
          accent ? 'text-primary' : 'text-foreground'
        )}
      >
        {icon}
        {who}
      </p>
      <div>
        <p className="font-semibold text-4xl tabular-nums tracking-tight">
          {formatDuration(speed.medianSeconds)}
        </p>
        <p className="mt-1 text-muted-foreground text-sm">typical reply</p>
      </div>
      <dl className="grid grid-cols-3 gap-3 border-t pt-4 text-sm">
        <div>
          <dt className="text-muted-foreground text-xs">First reply</dt>
          <dd className="font-semibold tabular-nums">
            {formatDuration(firstReply)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Replies</dt>
          <dd className="font-semibold tabular-nums">
            {formatCount(speed.replies)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">Over an hour</dt>
          <dd className="font-semibold tabular-nums">
            {formatCount(speed.overAnHour)}
          </dd>
        </div>
      </dl>
    </div>
  );
}

const BLOCK_LABELS = [
  '12am',
  '',
  '4am',
  '',
  '8am',
  '',
  '12pm',
  '',
  '4pm',
  '',
  '8pm',
  '',
];
const HEAT = [
  'bg-muted',
  'bg-primary/15',
  'bg-primary/30',
  'bg-primary/50',
  'bg-primary/75',
  'bg-primary',
];

function BusyTimes({ cells }: { cells: ClaireReport['busyTimes'] }) {
  const max = Math.max(1, ...cells.map((c) => c.messages));
  const at = (weekday: number, block: number) =>
    cells.find((c) => c.weekday === weekday && c.block === block)?.messages ??
    0;
  const shade = (n: number) =>
    n === 0 ? HEAT[0] : HEAT[Math.min(5, 1 + Math.floor((n / max) * 4.999))];

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-[2rem_repeat(12,minmax(0,1fr))] gap-0.5 text-[11px] text-muted-foreground sm:grid-cols-[2.25rem_repeat(12,minmax(0,1fr))] sm:gap-1">
        <span />
        {BLOCK_LABELS.map((label, block) => (
          <span key={block} className="whitespace-nowrap">
            {label}
          </span>
        ))}
        {WEEKDAYS.map((day, index) => (
          <Row key={day} day={day}>
            {BLOCK_LABELS.map((_, block) => {
              const n = at(index + 1, block);
              return (
                <span
                  key={block}
                  title={`${day} ${block * 2}:00–${block * 2 + 2}:00 · ${countOf(n, 'message')}`}
                  className={cn('h-6 rounded-[5px]', shade(n))}
                />
              );
            })}
          </Row>
        ))}
      </div>
      <div className="flex items-center justify-end gap-1 text-[11px] text-muted-foreground">
        Fewer
        {HEAT.map((tone) => (
          <span key={tone} className={cn('h-2.5 w-3.5 rounded-[3px]', tone)} />
        ))}
        More
      </div>
    </div>
  );
}

function Row({ day, children }: { day: string; children: ReactNode }) {
  return (
    <>
      <span className="self-center text-xs">{day}</span>
      {children}
    </>
  );
}
